import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ThumbsUp, Users, Sparkles, Crown, Gem, Wand2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { login, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Mafia", "Beastskin", "Single Mother", "Other"];
const GENDERS = ["male", "female", "non-binary"];

// Visual theme per rarity — matches the glow border we render on companion cards.
const RARITY_THEME = {
  normal:    { border: "border-white/10",         glow: "",                                         chip: "border-white/15 text-slate-300",     label: "Normal"    },
  rare:      { border: "border-sky-400/60",       glow: "shadow-[0_0_28px_rgba(56,189,248,0.25)]",   chip: "border-sky-400/60 text-sky-200",     label: "Rare"      },
  epic:      { border: "border-fuchsia-400/70",   glow: "shadow-[0_0_32px_rgba(217,70,239,0.3)]",   chip: "border-fuchsia-400/70 text-fuchsia-200", label: "Epic"  },
  legendary: { border: "border-amber-400/80",     glow: "shadow-[0_0_40px_rgba(251,191,36,0.4)]",   chip: "border-amber-400/80 text-amber-200", label: "Legendary" },
};

export default function SubmitCompanion() {
  const { user, refresh } = useAuth();
  const [subs, setSubs] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [form, setForm] = useState({
    name: "", trope: "", category: "Mafia", gender: "male",
    tagline: "", bio: "", voice: "", opening_line: "", image_url: "",
    rarity: "normal", ai_generated: false,
  });
  const [busy, setBusy] = useState(false);
  const [isSub, setIsSub] = useState(false);
  const [idea, setIdea] = useState("");
  const [drafting, setDrafting] = useState(false);

  const load = () => api.get("/companion-submissions").then((r) => setSubs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/companion-submissions/pricing").then((r) => {
      setPricing(r.data.tiers || []);
      setIsSub(!!r.data.is_subscriber);
    }).catch(() => {});
  }, [user]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const draftWithAi = async () => {
    if (!user) { login(); return; }
    if (idea.trim().length < 4) { toast.error("Describe your character in a few words first"); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/companion-submissions/ai-draft", {
        idea, category: form.category, gender: form.gender, with_portrait: true,
      });
      setForm((f) => ({ ...f, ...data, image_url: data.image_url || f.image_url, ai_generated: true }));
      toast.success("AI drafted your character — tweak anything you like");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally { setDrafting(false); }
  };

  const baseTier = pricing.find((t) => t.key === form.rarity) || { cost: 0, perks: "" };
  const selectedTier = { ...baseTier, cost: isSub ? 0 : baseTier.cost };
  const canAfford = !user ? true : (user.credits || 0) >= selectedTier.cost;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { login(); return; }
    for (const k of ["name", "trope", "tagline", "bio", "voice", "opening_line"]) {
      if (!form[k].trim()) { toast.error(`${k.replace("_", " ")} is required`); return; }
    }
    if (!canAfford) {
      toast.error(`Need ${selectedTier.cost} gems — you have ${user.credits || 0}.`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/companion-submissions", form);
      toast.success(
        selectedTier.cost > 0
          ? `Submitted for review — ${selectedTier.cost} gems held (refunded if declined)`
          : "Submitted for review — track it on your Dashboard"
      );
      setForm({ ...form, name: "", trope: "", tagline: "", bio: "", voice: "", opening_line: "", image_url: "", rarity: "normal", ai_generated: false });
      setIdea("");
      load();
      refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const vote = async (sid) => {
    try {
      await api.post(`/companion-submissions/${sid}/vote`);
      toast.success("Vote counted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vote failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="mb-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-rose-400 mb-2 flex items-center gap-2"><Users className="h-3 w-3" /> Submit a Companion</div>
        <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">Add your lead to the cast.</h1>
        <p className="mt-3 text-slate-400 max-w-2xl">Create your dream mafia don / shifter / mother — by hand or with AI. Every character is reviewed by our editors; approved leads join the Companions cast and the open ballot. {isSub ? <span className="text-emerald-300">Your subscription makes every tier free.</span> : <span>Subscribers submit free — others pay gems per tier.</span>}</p>
      </div>

      <div className="grid md:grid-cols-[1fr_1fr] gap-8">
        <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#0F1117] p-6 md:p-8 space-y-4">
          <div data-testid="sub-ai-panel" className="rounded-2xl border border-rose-400/30 bg-gradient-to-br from-rose-500/10 to-amber-500/5 p-4 space-y-3">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-rose-300"><Wand2 className="h-3.5 w-3.5" /> Create with AI</div>
            <Textarea data-testid="sub-ai-idea" value={idea} onChange={(e) => setIdea(e.target.value)} maxLength={400} rows={2} placeholder="e.g. a brooding lighthouse keeper who is secretly a selkie" className="bg-[#07080A] border-white/10" />
            <Button data-testid="sub-ai-draft" type="button" onClick={draftWithAi} disabled={drafting} variant="outline" className="w-full rounded-full border-rose-400/50 text-rose-200 hover:bg-rose-500/10 h-10">
              {drafting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Writing bio &amp; painting portrait… (~30s)</> : <><Sparkles className="h-4 w-4 mr-2" /> Draft bio + paint portrait</>}
            </Button>
            {form.image_url && (
              <img data-testid="sub-portrait-preview" src={fileUrl(form.image_url)} alt="Portrait preview" className="w-full max-h-72 object-cover object-top rounded-xl border border-white/10" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Name</Label>
              <Input data-testid="sub-name" value={form.name} onChange={update("name")} maxLength={60} className="mt-1 bg-[#07080A] border-white/10" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Trope</Label>
              <Input data-testid="sub-trope" value={form.trope} onChange={update("trope")} maxLength={80} placeholder="e.g. Rival Prince" className="mt-1 bg-[#07080A] border-white/10" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Category</Label>
              <Select value={form.category} onValueChange={update("category")}>
                <SelectTrigger data-testid="sub-category" className="mt-1 bg-[#07080A] border-white/10 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Gender</Label>
              <Select value={form.gender} onValueChange={update("gender")}>
                <SelectTrigger data-testid="sub-gender" className="mt-1 bg-[#07080A] border-white/10 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Tagline (1 line)</Label>
            <Input data-testid="sub-tagline" value={form.tagline} onChange={update("tagline")} maxLength={200} className="mt-1 bg-[#07080A] border-white/10" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Opening line (what they say first)</Label>
            <Textarea data-testid="sub-opening" value={form.opening_line} onChange={update("opening_line")} maxLength={280} rows={2} className="mt-1 bg-[#07080A] border-white/10" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Backstory bio</Label>
            <Textarea data-testid="sub-bio" value={form.bio} onChange={update("bio")} maxLength={600} rows={3} className="mt-1 bg-[#07080A] border-white/10" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Speaking style</Label>
            <Input data-testid="sub-voice" value={form.voice} onChange={update("voice")} maxLength={140} placeholder="e.g. gravelly, protective, Italian around the vowels" className="mt-1 bg-[#07080A] border-white/10" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Portrait URL (optional)</Label>
            <Input data-testid="sub-image" value={form.image_url} onChange={update("image_url")} placeholder="https://images.unsplash.com/…" className="mt-1 bg-[#07080A] border-white/10" />
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#07080A]/60 p-4 space-y-3" data-testid="sub-rarity-picker">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-amber-400" />
                <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Rarity tier</Label>
              </div>
              {user && (
                <div className="font-mono text-[10px] text-slate-400 flex items-center gap-1">
                  <Gem className="h-3 w-3 text-amber-400" />
                  <span className="text-amber-300">{user.credits ?? 0}</span>
                  <span>in wallet</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(pricing.length ? pricing : [{key:"normal",label:"Normal",cost:0,perks:""}]).map((t) => {
                const theme = RARITY_THEME[t.key] || RARITY_THEME.normal;
                const active = form.rarity === t.key;
                return (
                  <button
                    type="button"
                    key={t.key}
                    data-testid={`sub-rarity-${t.key}`}
                    onClick={() => setForm((f) => ({ ...f, rarity: t.key }))}
                    className={`text-left rounded-xl border p-3 transition-all ${theme.border} ${active ? `bg-white/5 ${theme.glow}` : "bg-black/30 hover:bg-white/5"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${theme.chip}`}>{t.label}</span>
                      {active && <div className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
                    </div>
                    <div className="mt-2 text-sm font-semibold text-slate-100">
                      {(isSub ? 0 : t.cost) === 0 ? "Free" : <span className="flex items-center gap-1"><Gem className="h-3 w-3 text-amber-400" /> {t.cost}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
            {selectedTier.perks && (
              <p className="text-[11px] text-slate-400 italic leading-snug">
                {selectedTier.perks}
              </p>
            )}
            {user && selectedTier.cost > 0 && !canAfford && (
              <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="sub-insufficient">
                You need <span className="font-mono text-rose-200">{selectedTier.cost - (user.credits || 0)}</span> more gems. <Link to="/pricing" className="underline hover:text-rose-200">Buy a pack</Link>.
              </div>
            )}
          </div>
          <Button data-testid="sub-submit" type="submit" disabled={busy || (user && !canAfford)} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11">
            {busy ? "Submitting…" : selectedTier.cost > 0 ? `Submit for review · ${selectedTier.cost} gems` : "Submit for review · Free"}
          </Button>
        </form>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-3 flex items-center gap-2"><Sparkles className="h-3 w-3" /> Approved cast · open ballot</div>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {subs.length === 0 && (
              <div className="rounded-2xl border border-dashed border-white/10 p-6 text-center text-slate-500 text-sm">
                No approved characters yet. Be the first.
              </div>
            )}
            {subs.map((s) => {
              const theme = RARITY_THEME[s.rarity || "normal"] || RARITY_THEME.normal;
              return (
              <div key={s.submission_id} data-testid={`submission-${s.submission_id}`} className={`rounded-2xl border bg-[#0F1117] p-4 flex gap-3 ${theme.border} ${theme.glow}`}>
                {s.image_url && (
                  <div className="h-16 w-14 flex-shrink-0 rounded-lg overflow-hidden border border-white/10">
                    <img src={fileUrl(s.image_url)} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-serif text-base font-semibold text-slate-100 truncate">{s.name}</div>
                    <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-rose-500/30 text-rose-300">{s.category}</span>
                    {s.rarity && s.rarity !== "normal" && (
                      <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${theme.chip}`} data-testid={`submission-rarity-${s.submission_id}`}>
                        {theme.label}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{s.tagline}</div>
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                    <span>by {s.author_name}</span>
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <button data-testid={`vote-${s.submission_id}`} onClick={() => vote(s.submission_id)} className="rounded-full p-2 border border-white/10 hover:border-rose-400/50 hover:text-rose-300 text-slate-400">
                    <ThumbsUp className="h-4 w-4" />
                  </button>
                  <div className="text-xs font-mono text-amber-300 mt-1">{s.votes}</div>
                </div>
              </div>
              );
            })}
          </div>
          <p className="mt-4 text-[11px] text-slate-500 font-mono uppercase tracking-widest">Voting is subscriber-only · top entry joins next month's cast</p>
        </div>
      </div>
    </div>
  );
}
